import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';
import CircleXmark from '@gef-ui/material-icons/CircleXmark';
import Eye from '@gef-ui/material-icons/Eye';
import EyeSlash from '@gef-ui/material-icons/EyeSlash';
import InboxOut from '@gef-ui/material-icons/InboxOut';

import { codelistProvider, ComponentLoader, Dialog } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import Subheader from 'components/atoms/GefMui/Subheader';
import SubmitFormDialog from 'components/atoms/GefMui/SubmitFormDialog/SubmitFormDialog';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import PAGES from 'constants/pages';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import campaignAdditionalData from 'features/views/campaignSettings/additionalData';
import campaignDetail from 'features/views/campaignSettings/campaignDetail';
import { deleteCampaignContacts } from 'features/views/campaignSettings/campaignDetail/actions';
import { CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME } from 'features/views/campaignSettings/campaignDetail/form';
import { activateAllRegions, deactivateAllRegions } from 'features/views/campaignSettings/guarantors/actions';
import startupCurve from 'features/views/campaignSettings/startupCurve';
import campaignSettingsTabs from 'features/views/campaignSettings/tabs';

import Campaign from '../Campaign';
import CampaignAdditionalData from '../CampaignAdditionalData';
import CampaignState from '../CampaignState';
import Guarantors from '../Guarantors';
import StartupCurve from '../StartupCurve';
import LoadSaveTemplateModal from './modals/LoadSaveTemplateModal';

const { tabs } = campaignSettingsTabs.constants;

export const CampaignSettings = ({ campaignId }) => {
	const dispatch = useDispatch();

	const activeTab = useSelector(campaignSettingsTabs.selectors.getActiveTab);
	const selectedCampaignDetail = useSelector(campaignDetail.selectors.getDetail);

	useEffect(
		() => () => {
			dispatch(startupCurve.actions.reset());
			dispatch(campaignSettingsTabs.actions.reset());
		},
		[dispatch],
	);

	const handleTabChange = useCallback(
		(e, tab) => {
			dispatch(campaignSettingsTabs.actions.setActiveTab(tab));
			dispatch(dialog.actions.close(CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME));
			dispatch(resetErrorsByPage({ page: CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME }));
			dispatch(dialog.actions.close(campaignAdditionalData.constants.NO_DESCRIPTION_ERROR));
		},
		[dispatch],
	);

	const handleLoadDetail = useCallback(() => {
		dispatch(campaignDetail.actions.fetchCampaignDetail(campaignId));
	}, [dispatch, campaignId]);

	const generateScreenInfo = useCallback(() => {
		const result = {};
		switch (activeTab) {
			case tabs.CAMPAIGN:
				result.screenId = PAGES.campaignSettings.campaign.id;
				result.screenName = PAGES.campaignSettings.campaign.name;
				break;
			case tabs.STARTUP_CURVE:
				result.screenId = PAGES.campaignSettings.startupCurve.id;
				result.screenName = PAGES.campaignSettings.startupCurve.name;
				break;
			case tabs.ADDITIONAL_DATA:
				result.screenId = PAGES.campaignSettings.additionalData.id;
				result.screenName = PAGES.campaignSettings.additionalData.name;
				break;
			case tabs.STATES:
				result.screenId = PAGES.campaignSettings.states.id;
				result.screenName = PAGES.campaignSettings.states.name;
				break;
			case tabs.GUARANTORS:
				result.screenId = PAGES.campaignSettings.guarantors.id;
				result.screenName = PAGES.campaignSettings.guarantors.name;
				break;
		}

		return result;
	}, [activeTab]);

	const { screenId, screenName } = generateScreenInfo();
	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: selectedCampaignDetail.fullName || campaignId,
		backButton: true,
	});

	const handleDeleteContacts = useCallback(() => {
		dispatch(deleteCampaignContacts(campaignId));
	}, [dispatch, campaignId]);

	const handleRevertChanges = useCallback(() => {
		dispatch(campaignAdditionalData.actions.revertChanges());
	}, [dispatch]);

	const isChanged = useSelector(campaignAdditionalData.selectors.getHasChanged);

	const handleActivateAllRegions = useCallback(() => {
		dispatch(activateAllRegions(campaignId));
	}, [dispatch, campaignId]);

	const handleDeactivateAllRegions = useCallback(() => {
		dispatch(deactivateAllRegions(campaignId));
	}, [dispatch, campaignId]);

	const subheaderToolbarBasedOnTab = useMemo(() => {
		let content;
		switch (activeTab) {
			case tabs.CAMPAIGN:
				content = (
					<ComponentLoader
						fetchStorePath={fetch.constants.urls.campaignDeleteContacts}
						onReload={handleDeleteContacts}
						ignoreErrors
					>
						<Button variant="text" startIcon={<InboxOut />} onClick={handleDeleteContacts}>
							{getLabelMessage('sidebar:restoreContacts')}
						</Button>
					</ComponentLoader>
				);
				break;
			case tabs.ADDITIONAL_DATA:
				content = (
					<ComponentLoader
						fetchStorePath={fetch.constants.urls.campaignAdditionalData}
						onReload={handleRevertChanges}
					>
						<Button
							variant="text"
							startIcon={<CircleXmark />}
							onClick={handleRevertChanges}
							disabled={!isChanged}
						>
							{getLabelMessage('sidebar:revertChanges')}
						</Button>
					</ComponentLoader>
				);
				break;
			case tabs.GUARANTORS:
				content = (
					<>
						<ComponentLoader
							fetchStorePath={`${fetch.constants.urls.campaignGuarantorActiveAllRegions}.put`}
							onReload={handleActivateAllRegions}
							ignoreErrors
						>
							<Button variant="text" startIcon={<Eye />} onClick={handleActivateAllRegions}>
								{getLabelMessage('guarantors:addAllRegions')}
							</Button>
						</ComponentLoader>
						<ComponentLoader
							fetchStorePath={`${fetch.constants.urls.campaignGuarantorActiveAllRegions}.delete`}
							onReload={handleDeactivateAllRegions}
							ignoreErrors
						>
							<Button variant="text" startIcon={<EyeSlash />} onClick={handleDeactivateAllRegions}>
								{getLabelMessage('guarantors:removeAllRegions')}
							</Button>
						</ComponentLoader>
					</>
				);
				break;
		}

		return (
			<Stack
				direction="row"
				spacing={1}
				sx={{
					justifyContent: 'flex-end',
				}}
			>
				{content}
			</Stack>
		);
	}, [
		activeTab,
		handleDeleteContacts,
		handleRevertChanges,
		isChanged,
		handleActivateAllRegions,
		handleDeactivateAllRegions,
	]);

	const floatingDialogs = (
		<>
			<SubmitFormDialog pageName={CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME} />
			<Dialog mui name={campaignAdditionalData.constants.NO_DESCRIPTION_ERROR}>
				{getLabelMessage('campaignAdditionalData:emptyDescriptionTitle')}
			</Dialog>
			<Dialog mui name={startupCurve.constants.dialogs.curveSequence}>
				{getLabelMessage('startupCurve:curveSequence')}
			</Dialog>
		</>
	);

	return (
		<TabContext value={activeTab}>
			<Panel screenId={screenId} screenName={screenName} floatingDialogs={floatingDialogs}>
				<LoadSaveTemplateModal id={MODALS.campaignSettings_templates} />
				<Paper sx={{ p: 3, borderRadius: 2 }}>
					<Stack spacing={2}>
						<Subheader title={subheaderTitle} headerToolbar={subheaderToolbarBasedOnTab} />
						<ComponentLoader
							fetchStorePath={fetch.constants.urls.campaignDetail}
							onReload={handleLoadDetail}
						>
							<TabList onChange={handleTabChange}>
								<Tab label={getLabelMessage('campaignSettings:campaign')} value={tabs.CAMPAIGN} />
								<Tab
									label={getLabelMessage('campaignSettings:startupCurve')}
									value={tabs.STARTUP_CURVE}
								/>
								<Tab
									label={getLabelMessage('campaignSettings:additionalData')}
									value={tabs.ADDITIONAL_DATA}
								/>
								<Tab label={getLabelMessage('campaignSettings:states')} value={tabs.STATES} />
								<Tab label={getLabelMessage('campaignSettings:guarantors')} value={tabs.GUARANTORS} />
							</TabList>
						</ComponentLoader>
					</Stack>
				</Paper>
				<>
					<TabPanel value={tabs.STATES} sx={{ p: 0 }}>
						<CampaignState campaignId={campaignId} />
					</TabPanel>
					<TabPanel value={tabs.CAMPAIGN} sx={{ p: 0 }}>
						<Campaign campaignId={campaignId} />
					</TabPanel>
					<TabPanel value={tabs.STARTUP_CURVE} sx={{ p: 0 }}>
						<StartupCurve />
					</TabPanel>
					<TabPanel value={tabs.ADDITIONAL_DATA} sx={{ p: 0 }}>
						<CampaignAdditionalData campaignId={campaignId} />
					</TabPanel>
					<TabPanel value={tabs.GUARANTORS} sx={{ p: 0 }}>
						<Guarantors campaignId={campaignId} />
					</TabPanel>
				</>
			</Panel>
		</TabContext>
	);
};

CampaignSettings.propTypes = {
	campaignId: PropTypes.string,
};

export default codelistProvider(codelists.parameters)(CampaignSettings);
